import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { normalizeError } from '@/lib/api';
import { formatDisplayCalendarDay } from '@/lib/date-format';
import {
  useCreatePlanningItem,
  useDuplicatePlanningItem,
  useUpdatePlanningItem,
  usePlanningWorkGroups,
  usePlanningActivities,
} from '@/hooks/use-planning';
import { useUsers } from '@/hooks/use-users';
import { useProjects } from '@/hooks/use-projects';
import { useLists } from '@/hooks/use-lists';
import type { PlanningItem, PlanningRole, PlanningShift } from '@/features/planning/types';

const SHIFT_OPTIONS: Array<{ label: string; value: PlanningShift }> = [
  { label: 'Morning', value: 'morning' },
  { label: 'Afternoon', value: 'afternoon' },
];

const ROLE_OPTIONS: Array<{ label: string; value: PlanningRole }> = [
  { label: 'Primary', value: 'primary' },
  { label: 'Cover', value: 'cover' },
];

type SourceType = 'project' | 'workgroup';
type ActivityType = 'list' | 'activity' | 'none';

interface PlanningItemFormProps {
  mode: 'create' | 'edit';
  weekId: string;
  weekDates: string[];
  item?: PlanningItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PlanningItemForm({ mode, weekId, weekDates, item, open, onOpenChange }: PlanningItemFormProps) {
  const createItem = useCreatePlanningItem();
  const duplicateItem = useDuplicatePlanningItem();
  const updateItem = useUpdatePlanningItem();
  const { data: users = [], isLoading: usersLoading } = useUsers();
  const { data: projects = [], isLoading: projectsLoading } = useProjects(false);
  const { data: workGroups = [], isLoading: workGroupsLoading } = usePlanningWorkGroups(false);
  const { data: activities = [], isLoading: activitiesLoading } = usePlanningActivities(false);

  const [agentId, setAgentId] = useState('');
  const [date, setDate] = useState('');
  const [sourceType, setSourceType] = useState<SourceType>('project');
  const [projectId, setProjectId] = useState('');
  const [workGroupId, setWorkGroupId] = useState('');
  const [activityType, setActivityType] = useState<ActivityType>('list');
  const [listId, setListId] = useState('');
  const [activityId, setActivityId] = useState('');
  const [shift, setShift] = useState<'none' | PlanningShift>('none');
  const [role, setRole] = useState<'none' | PlanningRole>('none');
  const [description, setDescription] = useState('');
  const [duplicateForRestOfWeek, setDuplicateForRestOfWeek] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [userChangedProject, setUserChangedProject] = useState(false);

  const itemListId = item?.listId ?? item?.list?.id;
  const itemListName = item?.list?.name ?? 'Current list';

  const {
    data: projectLists = [],
    isLoading: listsLoading,
  } = useLists(sourceType === 'project' && projectId ? projectId : undefined, false);

  const availableAgents = useMemo(
    () =>
      users
        .filter((user) => user.isActive && user.role !== 'client')
        .sort((a, b) =>
          (a.publicName ?? a.displayName).localeCompare(b.publicName ?? b.displayName),
        ),
    [users],
  );

  const availableProjects = useMemo(
    () => projects.filter((project) => !project.isArchived).sort((a, b) => a.name.localeCompare(b.name)),
    [projects],
  );

  const availableWorkGroups = useMemo(
    () => workGroups.filter((wg) => wg.isActive).sort((a, b) => a.name.localeCompare(b.name)),
    [workGroups],
  );

  const availableActivities = useMemo(
    () => activities.filter((a) => a.isActive).sort((a, b) => a.name.localeCompare(b.name)),
    [activities],
  );

  const availableLists = useMemo(() => {
    const filtered = projectLists.filter((list) => !list.isArchived).sort((a, b) => a.name.localeCompare(b.name));

    if (mode === 'edit' && !userChangedProject && itemListId && sourceType === 'project') {
      const hasItemList = filtered.some((list) => list.id === itemListId);
      if (!hasItemList) {
        return [{ id: itemListId, name: itemListName, projectId: projectId || item?.projectId || '', isArchived: false }, ...filtered];
      }
    }

    return filtered;
  }, [projectLists, mode, userChangedProject, itemListId, itemListName, projectId, item?.projectId, sourceType]);

  useEffect(() => {
    if (!open) return;

    setUserChangedProject(false);

    if (mode === 'edit' && item) {
      setAgentId(item.agentId);
      setDate(item.date.slice(0, 10));

      if (item.customWorkGroupId) {
        setSourceType('workgroup');
        setWorkGroupId(item.customWorkGroupId);
        setProjectId('');
      } else {
        setSourceType('project');
        setProjectId(item.projectId ?? item.project?.id ?? '');
        setWorkGroupId('');
      }

      if (item.customActivityId) {
        setActivityType('activity');
        setActivityId(item.customActivityId);
        setListId('');
      } else if (item.listId) {
        setActivityType('list');
        setListId(item.listId ?? item.list?.id ?? '');
        setActivityId('');
      } else {
        setActivityType('none');
        setListId('');
        setActivityId('');
      }

      setShift(item.shift ?? 'none');
      setRole(item.role ?? 'none');
      setDescription(item.description ?? '');
      setDuplicateForRestOfWeek(false);
    } else {
      setAgentId('');
      setDate(weekDates[0] ?? '');
      setSourceType('project');
      setProjectId('');
      setWorkGroupId('');
      setActivityType('list');
      setListId('');
      setActivityId('');
      setShift('none');
      setRole('none');
      setDescription('');
      setDuplicateForRestOfWeek(false);
    }
    setFormError(null);
  }, [open, weekDates, mode, item]);

  const handleSourceTypeChange = (value: SourceType) => {
    setSourceType(value);
    setProjectId('');
    setWorkGroupId('');
    setListId('');
    if (value === 'workgroup') {
      if (activityType === 'list') {
        setActivityType('activity');
      }
    }
  };

  const handleActivityTypeChange = (value: ActivityType) => {
    setActivityType(value);
    setListId('');
    setActivityId('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const hasProject = sourceType === 'project' && projectId;
    const hasWorkGroup = sourceType === 'workgroup' && workGroupId;

    if (!hasProject && !hasWorkGroup) {
      setFormError('Please select a project or work group.');
      return;
    }

    try {
      if (mode === 'edit') {
        if (!item) {
          setFormError('No planning item selected for editing.');
          return;
        }

        await updateItem.mutateAsync({
          id: item.id,
          weekId,
          data: {
            projectId: hasProject ? projectId : undefined,
            listId: activityType === 'list' && listId ? listId : undefined,
            customWorkGroupId: hasWorkGroup ? workGroupId : undefined,
            customActivityId: activityType === 'activity' && activityId ? activityId : undefined,
            shift: shift === 'none' ? null : shift,
            role: role === 'none' ? null : role,
            description: description.trim() || undefined,
          },
        });

        toast({ title: 'Planning item updated' });
      } else {
        if (!agentId || !date) {
          setFormError('Agent and date are required.');
          return;
        }

        await createItem.mutateAsync({
          weekId,
          agentId,
          date,
          projectId: hasProject ? projectId : undefined,
          listId: activityType === 'list' && listId ? listId : undefined,
          customWorkGroupId: hasWorkGroup ? workGroupId : undefined,
          customActivityId: activityType === 'activity' && activityId ? activityId : undefined,
          shift: shift === 'none' ? undefined : shift,
          role: role === 'none' ? undefined : role,
          description: description.trim() || undefined,
        });

        toast({ title: 'Planning item created' });
      }

      onOpenChange(false);
    } catch (error: unknown) {
      setFormError(normalizeError(error).message);
    }
  };

  const isEditMode = mode === 'edit';
  const sourceDataLoading = usersLoading || projectsLoading || workGroupsLoading || activitiesLoading;
  const submitPending = isEditMode ? updateItem.isPending : createItem.isPending;
  const duplicatePending = duplicateItem.isPending;
  const sourceDate = item?.date ? new Date(`${item.date.slice(0, 10)}T00:00:00Z`) : null;
  const isSourceSunday = !!sourceDate && sourceDate.getUTCDay() === 0;
  const duplicateDisabled =
    !isEditMode ||
    !item ||
    sourceDataLoading ||
    duplicatePending ||
    (duplicateForRestOfWeek && isSourceSunday);

  const handleDuplicate = async () => {
    if (!isEditMode || !item) return;

    setFormError(null);

    try {
      const result = await duplicateItem.mutateAsync({
        id: item.id,
        weekId,
        data: { duplicateForRestOfWeek },
      });

      if (result.createdCount === 0) {
        toast({
          title: result.message ?? 'No duplicates created',
        });
        return;
      }

      toast({
        title:
          result.createdCount === 1 ? 'Planning item duplicated' : `${result.createdCount} planning items duplicated`,
      });
      onOpenChange(false);
    } catch (error: unknown) {
      setFormError(normalizeError(error).message);
    }
  };

  const activityTypeOptions = sourceType === 'project'
    ? [
        { value: 'list' as const, label: 'Existing List' },
        { value: 'activity' as const, label: 'Custom Activity' },
        { value: 'none' as const, label: 'No Activity' },
      ]
    : [
        { value: 'activity' as const, label: 'Custom Activity' },
        { value: 'none' as const, label: 'No Activity' },
      ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit planning item' : 'Add planning item'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the project/work group, list/activity, and other details for this planning item.'
              : 'Select an agent, day, project/work group, and list/activity for this planning week.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {!isEditMode && (
              <div className="space-y-2">
                <Label>Agent</Label>
                <Select value={agentId} onValueChange={setAgentId} disabled={sourceDataLoading}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.publicName ?? agent.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!isEditMode && (
              <div className="space-y-2">
                <Label>Date</Label>
                <Select value={date} onValueChange={setDate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a day" />
                  </SelectTrigger>
                  <SelectContent>
                    {weekDates.map((weekDate) => (
                      <SelectItem key={weekDate} value={weekDate}>
                        {formatDisplayCalendarDay(weekDate)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Source Type</Label>
              <Select value={sourceType} onValueChange={handleSourceTypeChange} disabled={sourceDataLoading}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">Existing Project</SelectItem>
                  <SelectItem value="workgroup">Custom Work Group</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {sourceType === 'project' ? (
              <div className="space-y-2">
                <Label>Project</Label>
                <Select
                  value={projectId}
                  onValueChange={(nextProjectId) => {
                    setProjectId(nextProjectId);
                    setUserChangedProject(true);
                    setListId('');
                  }}
                  disabled={sourceDataLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProjects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Work Group</Label>
                <Select value={workGroupId} onValueChange={setWorkGroupId} disabled={sourceDataLoading}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a work group" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableWorkGroups.map((wg) => (
                      <SelectItem key={wg.id} value={wg.id}>
                        {wg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Activity Type</Label>
              <Select value={activityType} onValueChange={handleActivityTypeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {activityTypeOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {activityType === 'list' && sourceType === 'project' && (
              <div className="space-y-2">
                <Label>List</Label>
                <Select value={listId} onValueChange={setListId} disabled={!projectId || listsLoading}>
                  <SelectTrigger>
                    <SelectValue placeholder={projectId ? 'Select a list' : 'Select a project first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {activityType === 'activity' && (
              <div className="space-y-2">
                <Label>Activity</Label>
                <Select value={activityId} onValueChange={setActivityId} disabled={activitiesLoading}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an activity" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableActivities.map((activity) => (
                      <SelectItem key={activity.id} value={activity.id}>
                        {activity.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {activityType === 'none' && (
              <div className="space-y-2">
                <Label>Activity</Label>
                <div className="h-10 flex items-center text-sm text-muted-foreground">
                  No activity selected
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Shift (optional)</Label>
              <Select value={shift} onValueChange={(value: 'none' | PlanningShift) => setShift(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {SHIFT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Role (optional)</Label>
              <Select value={role} onValueChange={(value: 'none' | PlanningRole) => setRole(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {ROLE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description / Instructions (optional)</Label>
            <Textarea
              placeholder="Add notes or instructions for this planning item..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {sourceDataLoading && (
            <p className="text-sm text-muted-foreground">Loading data...</p>
          )}
          {formError && <p className="text-sm text-destructive">{formError}</p>}

          {isEditMode && (
            <div className="space-y-2 rounded-md border border-border/60 p-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="duplicate-rest-of-week"
                  checked={duplicateForRestOfWeek}
                  onCheckedChange={(checked) => setDuplicateForRestOfWeek(checked === true)}
                  disabled={duplicatePending}
                />
                <Label htmlFor="duplicate-rest-of-week" className="cursor-pointer text-sm font-medium">
                  Duplicate for rest of week
                </Label>
              </div>
              {duplicateForRestOfWeek && isSourceSunday && (
                <p className="text-xs text-muted-foreground">
                  There are no remaining days in this week to duplicate to.
                </p>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={() => void handleDuplicate()}
                disabled={duplicateDisabled}
              >
                {duplicatePending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {duplicateForRestOfWeek ? 'Duplicate for rest of week' : 'Duplicate'}
              </Button>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitPending || sourceDataLoading}>
              {submitPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitPending ? (isEditMode ? 'Saving...' : 'Creating...') : isEditMode ? 'Save changes' : 'Create item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
